import { User } from "@/entities/user";
import { updateUser } from "@/entities/user/api";
import { useAuthStore } from "@/features/auth";
import { regenerateCode, verify } from "@/features/auth/api";
import { enqueueSnackbar } from "notistack";
import React, { useState, ChangeEvent, useRef, useEffect } from "react";
import { Container, Col } from "react-bootstrap";
import { VerificationCodeInput } from "@/shared/ui";
import { ProfileEditForm, ProfileView } from "@/widgets/profile/ui";
import { filesUpload } from "@/entities/message";

export type UserWithPasswordFields = User & {
  currentPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
};

const ProfilePage = () => {
  const { user, setUser } = useAuthStore();
  const [isEditing, setIsEditing] = useState(false);
  const [formUser, setFormUser] = useState<UserWithPasswordFields | null>(null);
  const [validated, setValidated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [showVerification, setShowVerification] = useState(false);
  const [timer, setTimer] = useState(60);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (showVerification && timer > 0) {
      interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [showVerification, timer]);

  const handleRemoveAvatar = () => {
    setFormUser((prev) =>
      prev ? { ...prev, avatarUrl: undefined } : null
    );
  };

  const handleAvatarChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith("image/")) {
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("data", file);
      formData.append("profile", user?._id || '');

      const res = await filesUpload(formData);

      if (res.success && res.result) {
        setFormUser((prev) =>
          prev ? { ...prev, avatarUrl: res.result } : null
        );
      } else {
      }
    } catch (err) {
      console.error("Avatar upload error:", err);
      enqueueSnackbar("Ошибка при загрузке аватара.", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      setFormUser({
        ...user,
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    }
  }, [user]);

  // Meta
  useEffect(() => {
    const title = formUser?.firstName ? `${formUser.firstName}` : "Профиль";
    document.title = title;

    let meta = document.querySelector('meta[name="robots"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "robots");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", "noindex, nofollow");
  }, [formUser]);

  if (!user || !formUser) return null;

  const isValidPhone = (phone: string) => {
    const digits = String(phone).replace(/\D/g, "");
    return digits.length === 11 && (digits.startsWith("7") || digits.startsWith("8"));
  };

  const handleEditClick = () => {
    const digits = String(user.phone).replace(/\D/g, "");
    const normalizedPhone = digits.startsWith("7") ? digits : "7" + digits.slice(1);

    setFormUser({
      ...user,
      phone: normalizedPhone,
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setIsEditing(true);
    setValidated(false);
  };

  const handleCancel = () => {
    setFormUser({
      ...user,
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setIsEditing(false);
    setValidated(false);
    setShowVerification(false);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormUser((prev) => (prev ? { ...prev, [name]: value } : null));
  };

  const handlePhoneChange = (phone: string) => {
    setFormUser((prev) => (prev ? { ...prev, phone } : null));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formUser) return;

    setValidated(true);

    if (!isValidPhone(formUser.phone)) {
      enqueueSnackbar("Введите корректный номер телефона.", { variant: "error" });
      buttonRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    if (formUser.newPassword) {
      if (!formUser.currentPassword) {
        enqueueSnackbar("Введите текущий пароль.", { variant: "error" });
        return;
      }
      // if (formUser.newPassword.length < 6) {
      //   enqueueSnackbar("Новый пароль должен содержать не менее 6 символов.", { variant: "error" });
      //   return;
      // }
      if (formUser.newPassword !== formUser.confirmPassword) {
        enqueueSnackbar("Пароли не совпадают.", { variant: "error" });
        return;
      }
    }

    const payload: Record<string, any> = {};
    const hasNameChange = formUser.firstName !== user.firstName || formUser.lastName !== user.lastName;

    const hasPhoneChange = formUser.phone.toString().slice(1) !== user.phone.toString().slice(1);
    const hasPasswordChange = !!formUser.newPassword;
    const hasAvatarChange = formUser.avatarUrl !== user.avatarUrl;

    if (hasNameChange) {
      payload.firstName = formUser.firstName;
      payload.lastName = formUser.lastName;
    }
    if (hasAvatarChange) {
      payload.photo = formUser.avatarUrl;
    }
    if (hasPasswordChange) {
      payload.oldPassword = formUser.currentPassword;
      payload.newPassword = formUser.newPassword;
    }

    let normalizedPhone = formUser.phone;
    if (hasPhoneChange) {
      const digits = formUser.phone.replace(/\D/g, "");
      normalizedPhone = digits.startsWith("7") ? digits.replace(/^7/, "8") : digits;

      payload.phone = normalizedPhone;
    }


    if (!hasNameChange && !hasPhoneChange && !hasPasswordChange && !hasAvatarChange) {
      enqueueSnackbar("Нет изменений для сохранения.", { variant: "info" });
      setIsEditing(false);
      return;
    }

    setLoading(true);
    try {
      const data = await updateUser(payload);

      if (data.success) {
        if (hasPhoneChange && data.shouldVerify) {
          console.log("Should verify phone");
          setShowVerification(true);
          setTimer(60);
          enqueueSnackbar("Введите код подтверждения, отправленный по SMS.", { variant: "info" });
        } else {
          const updatedUser = { ...user, ...payload };
          setUser(updatedUser);
          enqueueSnackbar("Профиль успешно обновлён.", { variant: "success" });
          setIsEditing(false);
        }
      } else {
        enqueueSnackbar(data.error || "Ошибка при обновлении профиля.", { variant: "error" });
      }
    } catch (err) {
      // console.error("Update error:", err);
      // enqueueSnackbar("Не удалось обновить профиль.", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent<HTMLFormElement>, enteredCode: string) => {
    e.preventDefault();
    const digits = formUser.phone.replace(/\D/g, "");
    const phoneForVerify = digits.startsWith("7") ? digits.replace(/^7/, "8") : digits;

    setLoading(true);
    try {
      const data = await verify(phoneForVerify, +enteredCode);

      if (data.success) {
        const updatedUser = { ...formUser };
        delete updatedUser.currentPassword;
        delete updatedUser.newPassword;
        delete updatedUser.confirmPassword;
        setUser(updatedUser);
        enqueueSnackbar("Номер телефона подтверждён и обновлён.", { variant: "success" });
        setIsEditing(false);
        setShowVerification(false);
      } else {
        enqueueSnackbar(data.error || "Неверный код подтверждения.", { variant: "error" });
      }
    } catch (err) {
      console.error("Verification error:", err);
      enqueueSnackbar("Ошибка при подтверждении кода.", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = () => {
      const digits = formUser.phone.replace(/\D/g, "");
      const phoneForVerify = digits.startsWith("7") ? digits.replace(/^7/, "8") : digits;

      regenerateCode(phoneForVerify).then(data => {
        if (data.success) {
          enqueueSnackbar("Код запрошен повтрно!", { variant: "info" });
          setTimer(60)
        }
      })
  };

  if (!isEditing && !showVerification) {
    return <ProfileView user={formUser} onEdit={handleEditClick} />;
  }

  if (showVerification) {
    return (
      <Container className="mt-4 d-flex justify-content-center" style={{ maxWidth: 300 }}>
        <Col>
          <h2 className="text-center mb-4">Верификация</h2>
          <VerificationCodeInput
            onVerify={handleVerifyCode}
            onBack={() => {
              setShowVerification(false);
              setValidated(false);
            }}
            loading={loading}
            timer={timer}
            backTitle="Назад к редактированию"
            onResend={handleResendCode}
          />
        </Col>
      </Container>
    );
  }

  return (
    <ProfileEditForm
      formUser={formUser}
      handleSubmit={handleSubmit}
      handleChange={handleChange}
      handlePhoneChange={handlePhoneChange}
      handleAvatarChange={handleAvatarChange}
      handleRemoveAvatar={handleRemoveAvatar}
      showCurrentPassword={showCurrentPassword}
      setShowCurrentPassword={setShowCurrentPassword}
      showNewPassword={showNewPassword}
      setShowNewPassword={setShowNewPassword}
      showConfirmPassword={showConfirmPassword}
      setShowConfirmPassword={setShowConfirmPassword}
      isValidPhone={isValidPhone}
      validated={validated}
      loading={loading}
      onCancel={handleCancel}
      buttonRef={buttonRef}
    />
  );
};

export default ProfilePage;